import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, ShieldOff, ShieldQuestion, Lock, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const STATUS_CFG = {
  signed: { label: "Подпись подлинна — документ подписан", color: "text-blue-700 bg-blue-50 border-blue-200", icon: ShieldCheck },
  in_progress: { label: "Документ отправлен на подпись", color: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: ShieldQuestion },
  rejected: { label: "Подписание отклонено", color: "text-rose-700 bg-rose-50 border-rose-200", icon: ShieldOff },
  draft: { label: "Документ ещё не отправлен на подписание", color: "text-slate-600 bg-slate-100 border-slate-200", icon: ShieldQuestion },
};

function fmtTime(ts) {
  return ts ? ts.slice(0, 16).replace("T", " ") : "";
}

export default function VerifyDocument() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(code || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (c) => {
    if (!c) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/documents/verify/${c.trim()}`);
      setResult(data);
    } catch {
      setError("Документ с таким кодом проверки не найден");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (code) search(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const cfg = result ? STATUS_CFG[result.status] || STATUS_CFG.draft : null;
  const Icon = cfg?.icon;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="verify-document-page">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <Lock className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-slate-900 font-display">КриптоДок</span>
          <span className="text-xs text-slate-400 ml-2 px-2 py-0.5 rounded-full bg-slate-100">Публичная проверка подлинности подписи</span>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 mb-1">Проверка подлинности документа</h1>
            <p className="text-sm text-slate-500">Введите код проверки документа, указанный в документе или сопроводительном письме</p>
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="verify-code-input"
              placeholder="Например: KD-A1B2C3D4"
              value={input}
              onChange={(e) => setInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && input.trim() && navigate(`/verify/${input.trim()}`)}
            />
            <Button
              onClick={() => input.trim() && navigate(`/verify/${input.trim()}`)}
              disabled={!input.trim() || loading}
              data-testid="verify-code-submit-button"
              className="bg-slate-900 hover:bg-slate-800 shrink-0"
            >
              <Search className="w-4 h-4 mr-1.5" /> Проверить
            </Button>
          </div>

          {loading && (
            <div className="text-center text-slate-400 text-sm py-6" data-testid="verify-loading">
              Проверка…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-rose-600 text-sm bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5" data-testid="verify-error">
              <ShieldOff className="w-4 h-4" /> {error}
            </div>
          )}

          {result && (
            <div className="space-y-4" data-testid="verify-result">
              <div className={`flex items-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-lg border ${cfg.color}`} data-testid="verify-status-banner">
                <Icon className="w-5 h-5" /> {cfg.label}
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div>
                  <div className="text-xs text-slate-400 uppercase">Документ</div>
                  <div className="font-medium text-slate-800">{result.type} №{result.number}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase">Дата документа</div>
                  <div className="font-medium text-slate-800">{result.doc_date}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase">Организация</div>
                  <div className="font-medium text-slate-800">{result.organization_name}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase">Контрагент</div>
                  <div className="font-medium text-slate-800">{result.counterparty_name || "—"}</div>
                </div>
                {result.signed_by && (
                  <div className="col-span-2">
                    <div className="text-xs text-slate-400 uppercase">Подписал</div>
                    <div className="font-medium text-slate-800">{result.signed_by} — {fmtTime(result.signed_at)}</div>
                  </div>
                )}
                {result.reject_reason && (
                  <div className="col-span-2">
                    <div className="text-xs text-slate-400 uppercase">Причина отказа</div>
                    <div className="font-medium text-rose-700">{result.reject_reason}</div>
                  </div>
                )}
              </div>
              <div className="text-xs text-slate-400 font-mono" data-testid="verify-code-echo">Код проверки: {result.verification_code}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
